/**
 * CreateAccountForm Component
 * 
 * Renders the account creation form with fields for user information,
 * password setup, and account category preferences.
 * Includes real-time password validation and CAPTCHA verification.
 * 
 * @param {Object} props - Component props containing form state and handlers
 * @param {Object} props.newUser - User data object with all form fields
 * @param {Function} props.setNewUser - State setter for user data
 * @param {Function} props.handleRegistration - Callback to submit registration
 * @param {string} props.response - Alert message to display
 * @param {Function} props.checkPwdLength - Validates password length
 * @param {Function} props.checkPwdSymbol - Validates password symbols
 * @param {Function} props.checkPwdUppercase - Validates uppercase letters
 * @param {Function} props.checkPwdLowercase - Validates lowercase letters
 * @param {Function} props.checkPwdNum - Validates numbers
 * @param {Function} props.checkPwdMatch - Validates password confirmation
 * @param {Function} props.measurePwdLength - Returns password length
 * @param {string} props.captchaToken - CAPTCHA verification token
 * @param {Function} props.setCaptchaToken - Sets CAPTCHA token
 * @returns {JSX.Element} Create account form
 */
import React, { useState } from 'react';
import './create-account-form.css';
import { Card, Button, Form } from 'react-bootstrap';
import HeaderLogo from '../../../assets/navbar/roiquant-600x200px-white-01.png';
import { knowYouBetter } from '../dropdown-options/know-you-better';
import HearAboutUsForm from '../forms/hear-about-us-form';
import { Turnstile } from '@marsidev/react-turnstile';

function CreateAccountForm(props) {
  // Track password field visibility
  const [passwordShown, setPasswordShown] = useState(false);
  // Track confirm password field visibility
  const [cpasswordShown, setcPasswordShown] = useState(false);

  // Toggle show/hide password button
  const togglePassword = () => setPasswordShown(!passwordShown);
  // Toggle show/hide confirm password button
  const togglecPassword = () => setcPasswordShown(!cpasswordShown);

  // Handle form input changes and update state
  const handleChange = (e) => {
    const { name, value } = e.target;
    props.setNewUser(prev => ({ ...prev, [name]: value }));
  }

  // Track CAPTCHA completion status
  const [captchaCompleted, setCaptchaCompleted] = useState(false);



  return (
    <div id="createaccountcontent">
      <div className="createaccounttop">
        <a href="https://www.roiquant.com/" target="_blank" rel="noopener noreferrer">
          <img src={HeaderLogo} alt="roiquant" width="180px" height="60px" className="roiquantlogo" />
        </a>
      </div>

      {props.response}

      <div id="createaccountform">
        <Card className="createaccountcard">
          <div className="createaccountheader">
            <h3>Create free account</h3>
          </div>

          <Form>
            <p style={{ fontSize: "14px" }}>
              Please fill in all required fields marked with asterisks (<sup>*</sup>).
            </p>

            {/* First and Last Name Fields */}
            <div style={{ display: "flex", gap: "1.5%" }}>
              <Form.Group className="inputgroup" style={{ width: "50%" }}>
                <Form.Control required className="createaccinputbox" type="text" name="firstname" placeholder="First name*"
                  onChange={handleChange} />
              </Form.Group>
              <Form.Group className="inputgroup" style={{ width: "50%" }}>
                <Form.Control required className="createaccinputbox" type="text" name="lastname" placeholder="Last name*"
                  onChange={handleChange} />
              </Form.Group>
            </div>

            {/* Username and Email Address Fields */}
            <div style={{ display: "flex", gap: "1.5%" }}>
              <Form.Group className="inputgroup" style={{ width: "50%" }}>
                <Form.Control required className="createaccinputbox" type="text" name="username" placeholder="Username*"
                  onChange={handleChange} />
              </Form.Group>
              <Form.Group className="inputgroup" style={{ width: "50%" }}>
                <Form.Control required className="createaccinputbox" type="email" name="email" placeholder="Email*"
                  onChange={handleChange} />
              </Form.Group>
            </div>

            {/* Password and Confirm Password Fields */}
            <div style={{ display: "flex", gap: "1.5%" }}>
              <Form.Group className="inputgroup" style={{ width: "50%" }}>
                <Form.Control required className="createaccinputbox" type={passwordShown ? "text" : "password"} name="password" placeholder="Password*"
                  onChange={handleChange} />
                {/* Toggle to show/hide password */}
                <a href="#" className="showpassword" onClick={togglePassword}>{passwordShown ? "Hide" : "Show"} password</a>
              </Form.Group>
              <Form.Group className="inputgroup" style={{ width: "50%" }}>
                <Form.Control required className="createaccinputbox" type={cpasswordShown ? "text" : "password"} name="confirmPassword" placeholder="Confirm password*"
                  onChange={handleChange} />
                {/* Toggle to show/hide confirm password */}
                <a href="#" className="showpassword" onClick={togglecPassword}>{cpasswordShown ? "Hide" : "Show"} password</a>
              </Form.Group>
            </div>

            {/* Real-time password strength validation feedback */}
            <div style={{ width: "100%", display: "flex", flexDirection: "row", marginBottom: "1rem" }}>
              <div id="checksField" style={{ width: "50%", paddingLeft: "15%" }}>
                <p id="char" style={{ color: props.measurePwdLength(props.newUser.password) == 0 ? "grey" : props.checkPwdLength(props.newUser.password) ? "green" : "red" }}>At least 8 characters or as long as possible</p>

                <p id="char" style={{ color: props.measurePwdLength(props.newUser.password) == 0 ? "grey" : props.checkPwdSymbol(props.newUser.password) ? "green" : "red" }}>Include at least 1 symbol character</p>

                <p id="char" style={{ color: props.measurePwdLength(props.newUser.password) == 0 ? "grey" : props.checkPwdUppercase(props.newUser.password) ? "green" : "red" }}>At least 1 uppercase letter</p>
              </div>

              <div id="checksField" style={{ width: "50%" }} >
                <p id="char" style={{ color: props.measurePwdLength(props.newUser.password) == 0 ? "grey" : props.checkPwdNum(props.newUser.password) ? "green" : "red" }}>Include at least 1 number</p>

                <p id="char" style={{ color: props.measurePwdLength(props.newUser.password) == 0 ? "grey" : props.checkPwdLowercase(props.newUser.password) ? "green" : "red" }}>At least 1 lowercase letter</p>

                <p id="char" style={{ color: props.measurePwdLength(props.newUser.password) == 0 ? "grey" : props.checkPwdMatch(props.newUser.password, props.newUser.confirmPassword) ? "green" : "red" }}>Both passwords must match</p>
              </div>
            </div>

            {/* User Category Selection Dropdown */}
            <Form.Group className="inputgroup" >
              <Form.Label>
                We would like to know you better <sup className='text-danger'>*</sup>
              </Form.Label>
              <Form.Select className='createaccformbox' name="knowYouBetter" required onChange={handleChange}>
                <option value="">-- Select --</option>
                {knowYouBetter.map((option) => (
                  <option key={option.key} value={option.key}>{option.key}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Dynamic form fields based on how user heard about service */}
            <HearAboutUsForm
              subscription={props.newUser}
              setSubscription={props.setNewUser}
              handleChange={handleChange}
              // errors={errors}
              // touched={touched}
              isLoading={false}
            />

            <div className="extranote">
              <span>
                By clicking below, you agree to roiquant's
                <a href="https://viewpoints.roiquant.com/privacy-policy/" target="_blank" rel="noopener noreferrer"> privacy policy</a>,
                <a href="https://viewpoints.roiquant.com/how-do-we-use-cookies/" target="_blank" rel="noopener noreferrer"> cookies</a>,
                <a href="https://viewpoints.roiquant.com/user-notice/" target="_blank" rel="noopener noreferrer"> user notice</a>,
                <a href="https://viewpoints.roiquant.com/refund-policy/" target="_blank" rel="noopener noreferrer"> refund policy</a>, and
                <a href="https://viewpoints.roiquant.com/terms-of-service/" target="_blank" rel="noopener noreferrer"> terms of service</a>.
              </span>
            </div>

            {/* CAPTCHA verification to prevent bot registrations */}
            <Turnstile
              siteKey={process.env.REACT_APP_TURNSTILE}
              onSuccess={(token) => {
                setCaptchaCompleted(true);
                props.setCaptchaToken(token);
              }}
              onExpire={() => {
                setCaptchaCompleted(false);
                props.setCaptchaToken("");
              }}
              onError={() => {
                setCaptchaCompleted(false);
                props.setCaptchaToken("");
              }}
              options={{
                size: 'flexible', // or 'normal', or 'compact'
                theme: 'light',
              }}
            />

            {/* Submit button - only enabled after CAPTCHA completion */}
            <div style={{ textAlign: "center" }}>
              {captchaCompleted ?
                (<Button className="createaccountbtn glintanimation" onClick={() => props.handleRegistration({ ...props.newUser, captchaToken: props.captchaToken })}>
                  <span>Agree & create</span>
                </Button>) : <Button className="createaccountbtn glintanimation" disabled><span>Agree & create</span></Button>
              }

            </div>
          </Form>

          <div className="loginnote">
            <span>Already got an account? <a href="/login">Log in</a></span>
          </div>
        </Card>
      </div>
    </div >
  );
}

export default CreateAccountForm;
